import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';

const UsersContext = createContext();
export const useUsers = () => useContext(UsersContext);

const API_BASE = 'https://d3bdqxkpn753kf.cloudfront.net';
const CACHE_TTL = 10 * 60 * 1000; // 10 minutes

export const UsersProvider = ({ children }) => {
  const [users, setUsers] = useState([]);
  const isFetching = useRef(false);
  const lastFetchTime = useRef(null);
  const missedEmails = useRef(new Set());
  const debounceTimer = useRef(null);

  const fetchAllUsers = useCallback(() => {
    const token = localStorage.getItem('vrvToken');
    if (!token || isFetching.current) return;
    isFetching.current = true;
    fetch(`${API_BASE}/vrv-users-getAll`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => {
        const parsed = typeof data.body === 'string' ? JSON.parse(data.body) : data;
        setUsers(parsed.users || []);
        lastFetchTime.current = Date.now();
        missedEmails.current.clear();
      })
      .catch(() => {})
      .finally(() => { isFetching.current = false; });
  }, []);

  // Initial load
  useEffect(() => {
    const token = localStorage.getItem('vrvToken');
    if (token) fetchAllUsers();
  }, [fetchAllUsers]);

  // TTL refresh — check every minute, re-fetch if cache older than 10 mins
  useEffect(() => {
    const interval = setInterval(() => {
      if (lastFetchTime.current && Date.now() - lastFetchTime.current > CACHE_TTL) {
        fetchAllUsers();
      }
    }, 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchAllUsers]);

  // Re-fetch when profile is updated
  useEffect(() => {
    window.addEventListener('vrv-profile-updated', fetchAllUsers);
    return () => window.removeEventListener('vrv-profile-updated', fetchAllUsers);
  }, [fetchAllUsers]);

  const getUserName = useCallback((email) => {
    if (!email) return '';
    const u = users.find(u => u.emailID === email);
    if (u) return `${u.name || ''} ${u.surname || ''}`.trim() || email;

    // Cache miss — debounce batch refresh (collect all misses, fetch once after 500ms)
    if (!missedEmails.current.has(email)) {
      missedEmails.current.add(email);
      clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => { fetchAllUsers(); }, 500);
    }

    return null; // caller shows loading indicator
  }, [users, fetchAllUsers]);

  const getUserObj = useCallback((email) => {
    if (!email) return null;
    return users.find(u => u.emailID === email) || null;
  }, [users]);

  return (
    <UsersContext.Provider value={{ users, getUserName, getUserObj }}>
      {children}
    </UsersContext.Provider>
  );
};
